import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { inviteInfo, acceptInvite } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { toast } from "sonner";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token"); return; }
    inviteInfo(token).then(setInfo).catch(() => setError("This invitation is invalid or has already been used."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await acceptInvite(token, password);
      await refreshUser();
      toast.success("Account created — welcome to Vozelto");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete signup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <Logo variant="horizontal" height={40} />
          <span className="font-head text-2xl font-semibold sr-only">Vozelto</span>
        </div>
        <div className="card-matte p-8" data-testid="accept-invite-card">
          {error ? (
            <div className="text-center"><p className="text-red-400 text-sm" data-testid="invite-error">{error}</p>
              <Button onClick={() => navigate("/login")} className="mt-4 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Go to sign in</Button></div>
          ) : !info ? (
            <p className="text-[#9a9a9a] text-sm ai-thinking">Loading invitation…</p>
          ) : (
            <>
              <h1 className="font-head text-xl font-semibold mb-1">Welcome, {info.name}</h1>
              <p className="text-sm text-[#9a9a9a] mb-6">You've been invited as <span className="brand-text font-semibold">{info.role.replace("_", " ")}</span>. Create a password for <span className="text-[#cfcfcf]">{info.email}</span>.</p>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="label-caps">Create password</Label>
                  <Input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" className="bg-[#10101A] border-white/10 mt-1" /></div>
                <Button data-testid="invite-submit" type="submit" disabled={busy} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">{busy ? "Creating…" : "Create account & sign in"}</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
